import React, {useCallback, useMemo, useRef, useState} from 'react';
import TemplateDefault from "../../components/template-default";
import Card from "../../components/card";
import Button from "../../components/button";
import {FaEraser, FaPlusSquare} from "react-icons/fa";
import SelectComponent from "../../components/select-component";
import InputForm from "../../components/input-form";
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../utils/localization-ag-grid";
import {useNavigate} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import attendancesService from "../../services/attendances-service";
import statusCellRenderer from "../hardware-attendances/components/status-cell-renderer";
import {buildAccentInsensitiveColDef, setAccentInsensitiveQuickFilter} from "../../utils/ag-grid-quick-filter";

const initialState = {value: "", label: "Todos"}

const Attendance = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState(initialState);

    const {data} = useQuery({
        queryFn: () => attendancesService.list(status).then(({data}) => data),
        queryKey: ["attendances", {status}]
    });

    const compareDate = (date1, date2) => {
        const formatDate = (givenDate) => {
            if (!givenDate) {
                return null;
            }
            const [datePart, timePart] = givenDate.split(' ');
            const [day, month, year] = datePart.split('/');
            return new Date(`${year}-${month}-${day}T${timePart}:00`);
        };

        const firstDate = formatDate(date1);
        const secondDate = formatDate(date2);

        if (!firstDate && !secondDate) {
            return 0;
        }
        if (!firstDate || firstDate > secondDate) {
            return 1;
        }
        if (!secondDate || firstDate < secondDate) {
            return -1;
        }
        return 0;
    };

    const [columnDefs] = useState([
        { field: "id", headerName: "ID", width: 70 },
        { field: "initial_format", headerName: "Abertura", comparator: compareDate },
        { field: "status", headerName: "Status", cellRenderer: statusCellRenderer },
        { field: "product_place", headerName: "Produto (Localização)", flex: 1, minWidth: 250 },
        { field: "requester", headerName: "Solicitante" },
        { field: "resource", headerName: "Tipo" }
    ]);

    const gridRef = useRef();
    const searchRef = useRef();

    const defaultColDef = useMemo(() => buildAccentInsensitiveColDef({resizable: false}), []);

    const onCellClicked = ({data}) => {
        const type = data.resource === 'Infraestrutura' ? 'infra' : 'software'
        navigate(`/attendances/form/${data.id}?type=${type}`);
    }

    const onFilterTextBoxChanged = useCallback((target) => {
        setAccentInsensitiveQuickFilter(gridRef, target.value);
    }, []);

    const clearFilters = () => {
        setStatus(initialState);
        searchRef.current.value = "";
        onFilterTextBoxChanged(searchRef.current);
    }

    return (
        <TemplateDefault title="Lista de chamados">
            <Card>
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                    <Button col="col-span-2" onClick={() => navigate("/attendances/form")}>
                        <FaPlusSquare/> Novo chamado
                    </Button>

                    <div className="col-span-3 text-gray-900">
                        <SelectComponent
                            instanceId="status"
                            label="Status"
                            options={[
                                {value: "", label: "Todos"},
                                {value: "Aberto", label: "Aberto"},
                                {value: "Encerrado", label: "Encerrado"},
                            ]}
                            value={status}
                            onChange={(value) => setStatus(value)}
                            name="status"
                            isClearable={false}
                        />
                    </div>

                    <div className="col-span-7">
                        <div className="flex justify-between">
                            <InputForm
                                id="searchData" label="Pesquisar" col="w-[calc(100%-60px)]" type="search"
                                onChange={({target}) => onFilterTextBoxChanged(target)}
                                reference={searchRef}
                            />
                            <div>
                                <Button onClick={clearFilters}>
                                    <FaEraser/>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={"ag-theme-quartz grid mt-4"} style={{height: "calc(100vh - 185px)"}}>
                    <AgGridReact
                        ref={gridRef}
                        rowHeight={32}
                        rowData={data ?? []}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        pagination
                        paginationPageSize={500}
                        paginationPageSizeSelector={[20, 50, 100, 500]}
                        animateRows={false}
                        suppressRowClickSelection={true}
                        suppressCellFocus={true}
                        onCellClicked={onCellClicked}
                        gridOptions={{localeText: LocalizationAgGrid}}
                    />
                </div>
            </Card>
        </TemplateDefault>
    );
};

export default Attendance;
